// react
import {
	ChangeEvent,
	Dispatch,
	SetStateAction,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';

// react-router-dom
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';
import { ContextSpendPlan } from '../../pages/ProjectV2/ProjectV2.context';
import { ContextUsersPermissions } from '../../pages/Project/Project.context';

// i18n
import { useTranslation } from 'react-i18next';

// hooks
import { useScrollToError } from '../../../hooks/useScrollToError';

// mui
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	Divider,
	FormControl,
	FormControlLabel,
	InputAdornment,
	MenuItem,
	Paper,
	Radio,
	RadioGroup,
	SelectChangeEvent,
	Stack,
	Step,
	StepLabel,
	Stepper,
	TextField,
	Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

// icons
import {
	Close as IconClose,
	Description as IconDescription,
	Info as IconInfo,
	Payment as IconPayment,
	NavigateNext as IconNext,
	NavigateBefore as IconBack,
} from '@mui/icons-material';

// types
import { ConditionPayload, Condition, VerificationSection } from '../../../@types';

// utils
import { formatCurrency } from '../../../utils/currency';
import {
	getContractCommitmentAmount,
	getContractNum,
	getContractSupplierName,
} from '../../pages/ProjectV2/Contracts/Contracts.utils';

// reusable condition settings editor
import { ConditionSettingsEditor } from '../../ui/ConditionSettingsEditor/ConditionSettingsEditor';

// AI sub-components for the wizard
import { ContractDocumentsSection } from '../../ui/ContractDocumentsSection/ContractDocumentsSection';
import { AiConditionGenerator } from '../../ui/AiConditionGenerator/AiConditionGenerator';

// Props & Types

export type ConditionType = 'workPlan' | 'contract' | '';
export type PaymentType = 'fixed' | 'percentage';

export interface FormAddConditionValues {
	activityId: number | '';
	conditionType: ConditionType;
	contractId: number | '';
	description: string;
	workPlanId: number | '';
	hasPaymentTerms: boolean;
	name: string;
	paymentAmount: number | '';
	paymentPercentage: number | '';
	paymentType: PaymentType;
	sequenceOrder: number | '';
	verificationSections: VerificationSection[];
}

export interface FormAddConditionProps {
	condition?: Condition | null;
	onClose?: () => void;
	onSuccess?: () => void;
	preSelectedContext?: {
		activityId?: number;
		contractId?: number;
		workPlanId?: number;
	};
	setIsLoading?: Dispatch<SetStateAction<boolean>>;
}

// Wizard Steps

interface WizardStep {
	key: string;
	label: string;
	labelKey?: string;
	icon: React.ReactNode;
	isApplicable: (values: FormAddConditionValues) => boolean;
}

const CONDITION_NAME_PRESETS = [
	'Advance Payment',
	'Progress Payment',
	'Final Payment',
	'Retention Release',
	'Milestone Completion',
	'Document Submission',
	'Performance Guarantee',
	'Compliance Verification',
	'Deliverable Acceptance',
	'Bank Guarantee',
];

const WIZARD_STEPS: WizardStep[] = [
	{
		key: 'details',
		label: 'Milestone Details',
		labelKey: 'spendPlan.conditions.wizard.step1',
		icon: <IconDescription />,
		isApplicable: () => true,
	},
	{
		key: 'paymentAndConditions',
		label: 'Payment Terms & Conditions',
		labelKey: 'spendPlan.conditions.wizard.step2',
		icon: <IconPayment />,
		isApplicable: () => true,
	},
	{
		key: 'review',
		label: 'Review & Confirm',
		labelKey: 'spendPlan.conditions.wizard.step3',
		icon: <IconInfo />,
		isApplicable: () => true,
	},
];

// Component

export const FormAddCondition = ({
	condition,
	onClose,
	onSuccess,
	preSelectedContext,
	setIsLoading,
}: FormAddConditionProps) => {
	// hooks
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// URL params for pre-selection
	const paramDialogContract = searchParams.get('dialogContract');
	const paramDialogWorkPlan = searchParams.get('dialogWorkPlan');
	const paramDialogActivity = searchParams.get('dialogActivity');
	// context
	const { setBanner } = useContext(ContextBanner);
	const { activities, conditions, contracts, createCondition, workPlans, updateCondition } =
		useContext(ContextSpendPlan);
	const { usersPermissions: projectUsers } = useContext(ContextUsersPermissions);

	// derived
	const isEditMode = Boolean(condition);

	// Helper to get work plan ID from condition
	const getWorkPlanIdFromCondition = (c: Condition): number | '' => {
		if (typeof c.workPlan === 'number') return c.workPlan;
		if (c.workPlan && typeof c.workPlan === 'object') return c.workPlan.id;
		return '';
	};

	// Helper to get activity ID from condition
	const getActivityIdFromCondition = (c: Condition): number | '' => {
		if (typeof c.activity === 'number') return c.activity;
		if (c.activity && typeof c.activity === 'object') return c.activity.id;
		return '';
	};

	// Helper to get contract ID from condition
	const getContractIdFromCondition = (c: Condition): number | '' => {
		if (typeof c.contract === 'number') return c.contract;
		if (c.contract && typeof c.contract === 'object') {
			// `contractId` fallback covers legacy payloads that nested the id
			// under a different field name.
			const legacyId = (c.contract as { contractId?: number }).contractId;
			return c.contract.id || legacyId || '';
		}
		return '';
	};

	// Check if hierarchy context is already known (from URL params, props, or edit within a contract page)
	const hasPrePopulatedContext = useMemo(() => {
		return Boolean(
			paramDialogWorkPlan ||
			paramDialogActivity ||
			paramDialogContract ||
			preSelectedContext?.contractId
		);
	}, [paramDialogWorkPlan, paramDialogActivity, paramDialogContract, preSelectedContext]);

	// state - wizard step
	const [activeStep, setActiveStep] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState(false);

	// state - form values
	const [values, setValues] = useState<FormAddConditionValues>(() => {
		if (condition) {
			const uiConditionType: ConditionType =
				condition.conditionType === 'payment' ? 'contract' : 'workPlan';
			const paymentType: PaymentType = condition.paymentPercentage ? 'percentage' : 'fixed';
			const hasPaymentTerms =
				uiConditionType === 'workPlan' &&
				(!!condition.paymentAmount || !!condition.paymentPercentage);
			return {
				activityId: getActivityIdFromCondition(condition),
				conditionType: uiConditionType,
				contractId: getContractIdFromCondition(condition),
				description: condition.description || '',
				workPlanId: getWorkPlanIdFromCondition(condition),
				hasPaymentTerms,
				name: condition.name || '',
				paymentAmount: condition.paymentAmount || '',
				paymentPercentage: condition.paymentPercentage || '',
				paymentType,
				sequenceOrder: condition.sequenceOrder || '',
				verificationSections: condition.verificationSections || [],
			};
		}

		// URL param pre-selection
		const initialWorkPlan = paramDialogWorkPlan
			? parseInt(paramDialogWorkPlan)
			: preSelectedContext?.workPlanId || '';
		const initialActivity = paramDialogActivity
			? parseInt(paramDialogActivity)
			: preSelectedContext?.activityId || '';
		const initialContract = paramDialogContract
			? parseInt(paramDialogContract)
			: preSelectedContext?.contractId || '';

		const defaults: FormAddConditionValues = {
			activityId: initialActivity || '',
			conditionType: 'contract' as ConditionType,
			contractId: initialContract || '',
			description: '',
			workPlanId: initialWorkPlan || '',
			hasPaymentTerms: false,
			name: '',
			paymentAmount: '',
			paymentPercentage: '',
			paymentType: 'percentage',
			sequenceOrder: '',
			verificationSections: [],
		};

		// Check for AI-generated initial values from the AI Condition Builder
		try {
			const aiValuesJson = sessionStorage.getItem('aiConditionInitialValues');
			if (aiValuesJson) {
				sessionStorage.removeItem('aiConditionInitialValues');
				const aiValues = JSON.parse(aiValuesJson) as Partial<FormAddConditionValues>;
				return {
					...defaults,
					...aiValues,
					// Preserve hierarchy fields from URL params (AI doesn't set these)
					workPlanId: defaults.workPlanId,
					activityId: defaults.activityId,
					contractId: defaults.contractId,
				};
			}
		} catch {
			// Ignore parse errors — fall through to defaults
		}

		return defaults;
	});

	// state - validation errors
	const [errors, setErrors] = useState<Partial<Record<keyof FormAddConditionValues, string>>>({});
	const [error, setError] = useState<string | null>(null);

	// state - AI document insights (populated by ContractDocumentsSection)
	const [insightsLoading, setInsightsLoading] = useState(false);
	const [documentInsights, setDocumentInsights] = useState<Array<{
		persona: string;
		summary: string;
		keyInformationTable?: Record<string, string>;
		checklist?: string[];
		disbursementConditions?: string;
		timelines?: string;
		responsibleParties?: string;
		keyRisks?: string;
	}> | null>(null);

	// Handler: receive document insights from ContractDocumentsSection
	const handleDocumentInsightsLoaded = (insights: typeof documentInsights) => {
		setDocumentInsights(insights);
	};

	// Handler: append AI-generated conditions as a grouped "AI Generated Conditions" section
	const handleUseAiConditions = (aiSections: VerificationSection[]) => {
		setValues((prev) => {
			// Remove any existing AI-generated section so we don't duplicate
			const manualSections = prev.verificationSections.filter(
				(s) => s.name !== 'AI Generated Conditions'
			);
			return {
				...prev,
				verificationSections: [...manualSections, ...aiSections],
			};
		});
		setBanner({
			message: t('spendPlan.conditions.wizard.aiSectionsAdded', { count: aiSections.length }),
			severity: 'success',
			hasClose: true,
		});
	};

	// Sync form values when condition prop changes
	useEffect(() => {
		if (!condition) return;
		const uiConditionType: ConditionType =
			condition.conditionType === 'payment' ? 'contract' : 'workPlan';
		const paymentType: PaymentType = condition.paymentPercentage ? 'percentage' : 'fixed';
		const hasPaymentTerms =
			uiConditionType === 'workPlan' &&
			(!!condition.paymentAmount || !!condition.paymentPercentage);
		setValues({
			activityId: getActivityIdFromCondition(condition),
			conditionType: uiConditionType,
			contractId: getContractIdFromCondition(condition),
			description: condition.description || '',
			workPlanId: getWorkPlanIdFromCondition(condition),
			hasPaymentTerms,
			name: condition.name || '',
			paymentAmount: condition.paymentAmount || '',
			paymentPercentage: condition.paymentPercentage || '',
			paymentType,
			sequenceOrder: condition.sequenceOrder || '',
			verificationSections: condition.verificationSections || [],
		});
	}, [condition]);

	const errorRef = useScrollToError(error);

	// Derived data

	const selectedWorkPlan = values.workPlanId
		? workPlans.find((e) => e.id === values.workPlanId)
		: null;

	const selectedActivity = values.activityId
		? activities.find((a) => a.id === values.activityId)
		: null;

	const selectedContract = values.contractId
		? contracts.find((c) => c.id === values.contractId)
		: null;

	const contractWorkPlan = selectedContract
		? workPlans.find((e) => e.id === selectedContract.workPlan)
		: null;

	const activeWorkPlan = selectedWorkPlan || contractWorkPlan;

	const filteredActivities = values.workPlanId
		? activities.filter((a) => {
				const actWorkPlanId = typeof a.workPlan === 'number' ? a.workPlan : a.workPlan?.id;
				return actWorkPlanId === values.workPlanId;
			})
		: activities;

	const filteredContracts = useMemo(() => {
		if (!values.activityId) return contracts;
		return contracts.filter((c) => {
			if (values.contractId && c.id === values.contractId) return true;
			return c.planningActivity === values.activityId;
		});
	}, [contracts, values.activityId, values.contractId]);

	const existingConditionsCount = conditions.filter((c) => {
		if (values.contractId) {
			const condContractId = typeof c.contract === 'number' ? c.contract : c.contract?.id;
			return condContractId === values.contractId;
		}
		if (values.workPlanId && !values.contractId) {
			const condWorkPlanId = typeof c.workPlan === 'number' ? c.workPlan : c.workPlan?.id;
			return condWorkPlanId === values.workPlanId && !c.contract;
		}
		return false;
	}).length;

	// Running payment total: sum of all payment conditions on the selected contract
	const contractPaymentTotal = useMemo(() => {
		if (!values.contractId || !selectedContract) return { totalPercentage: 0, totalAmount: 0 };
		const contractConditions = conditions.filter((c) => {
			const condContractId = typeof c.contract === 'number' ? c.contract : c.contract?.id;
			// Exclude current condition in edit mode
			if (isEditMode && condition && c.id === condition.id) return false;
			return condContractId === values.contractId;
		});
		const totalPercentage = contractConditions.reduce(
			(sum, c) => sum + (c.paymentPercentage || 0),
			0
		);
		const totalAmount = contractConditions.reduce((sum, c) => sum + (c.paymentAmount || 0), 0);
		return { totalPercentage, totalAmount };
	}, [conditions, values.contractId, selectedContract, isEditMode, condition]);

	// Wizard step management

	const applicableSteps = useMemo(
		() => WIZARD_STEPS.filter((step) => step.isApplicable(values)),
		[values]
	);

	const currentStepDef = applicableSteps[activeStep] || applicableSteps[0];

	const canGoNext = useMemo(() => {
		if (!currentStepDef) return false;
		switch (currentStepDef.key) {
			case 'details': {
				if (!values.name.trim()) return false;
				// Payment conditions need all three parent IDs — but when the hierarchy
				// is pre-populated (e.g. opened from the Contract page) the selectors are
				// hidden, so we can't require fields the user can't see/fix. Trust the
				// contractId in that case.
				if (values.conditionType === 'contract') {
					if (hasPrePopulatedContext) {
						return Boolean(values.contractId);
					}
					return Boolean(values.workPlanId && values.activityId && values.contractId);
				}
				// Base conditions need workPlanId
				if (values.conditionType === 'workPlan') {
					return Boolean(values.workPlanId);
				}
				return true;
			}
			case 'paymentAndConditions':
				return true; // payment and verification are optional
			case 'customFields':
				return true; // custom fields validated on submit
			case 'review':
				return true;
			default:
				return true;
		}
	}, [currentStepDef, values, hasPrePopulatedContext]);

	const handleNext = () => {
		if (activeStep < applicableSteps.length - 1) {
			setActiveStep((prev) => prev + 1);
		}
	};

	const handleBack = () => {
		if (activeStep > 0) {
			setActiveStep((prev) => prev - 1);
		}
	};

	const handleStepClick = (stepIndex: number) => {
		// Allow clicking on any previous step or current step in edit mode
		if (isEditMode || stepIndex <= activeStep) {
			setActiveStep(stepIndex);
		}
	};

	// Generic handlers

	const handleChange = (
		e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>
	) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues((prev) => ({ ...prev, [name]: value }));
		if (errors[name as keyof FormAddConditionValues]) {
			setErrors((prev) => ({ ...prev, [name]: undefined }));
		}
	};

	const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, checked } = e.target;
		setValues((prev) => ({ ...prev, [name]: checked }));
	};

	// Submit

	const handleSubmit = async () => {
		setError(null);
		setIsSubmitting(true);

		const newErrors: Partial<Record<keyof FormAddConditionValues, string>> = {};
		if (!values.name.trim()) newErrors.name = 'Milestone name is required';
		if (values.conditionType === 'contract') {
			// When the hierarchy selectors are hidden (pre-populated context), we can't
			// require fields the user can't see. Trust the contractId in that case.
			if (hasPrePopulatedContext) {
				if (!values.contractId) newErrors.contractId = 'Contract is required';
			} else {
				if (!values.workPlanId) newErrors.workPlanId = 'Work plan is required';
				if (!values.activityId) newErrors.activityId = 'Activity is required';
				if (!values.contractId) newErrors.contractId = 'Contract is required';
			}
		} else if (values.conditionType === 'workPlan') {
			if (!values.workPlanId) newErrors.workPlanId = 'Work plan is required';
		}

		// Sequence order, when provided, must be a positive integer — the server
		// rejects zero/negative values (schema requires `.positive()`). Catch it
		// here with a clear inline message instead of a cryptic INVALID_SCHEMA.
		if (values.sequenceOrder !== '' && Number(values.sequenceOrder) <= 0) {
			newErrors.sequenceOrder = 'Sequence must be a positive number';
		}

		// Payment milestones must carry the rule that defines what's owed —
		// either a fixed amount or a percentage of the contract commitment.
		// Without one, the Prepare-Payment dialog has nothing to compute the
		// gross / tax / net from when the voucher is finalized.
		const shouldRequirePayment =
			values.conditionType === 'contract' ||
			(values.conditionType === 'workPlan' && values.hasPaymentTerms);
		if (shouldRequirePayment) {
			if (
				values.paymentType === 'fixed' &&
				(values.paymentAmount === '' || Number(values.paymentAmount) <= 0)
			) {
				newErrors.paymentAmount = 'Payment amount is required';
			}
			if (
				values.paymentType === 'percentage' &&
				(values.paymentPercentage === '' || Number(values.paymentPercentage) <= 0)
			) {
				newErrors.paymentPercentage = 'Payment percentage is required';
			}
		}

		// Validate all requirement fields have labels (across all sections)
		const allFields = values.verificationSections.flatMap((s) => s.fields);
		const emptyLabelField = allFields.find((f) => !f.label.trim());
		if (emptyLabelField) {
			setError(
				`All requirement fields must have a label. Please fill in the missing label for the ${emptyLabelField.fieldType} field.`
			);
			// Go to payment & conditions step
			const paymentIdx = applicableSteps.findIndex((s) => s.key === 'paymentAndConditions');
			if (paymentIdx >= 0) setActiveStep(paymentIdx);
			setIsSubmitting(false);
			return;
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			setIsSubmitting(false);
			if (newErrors.name) setActiveStep(0);
			if (newErrors.workPlanId || newErrors.activityId || newErrors.contractId) setActiveStep(0);
			if (newErrors.sequenceOrder) setActiveStep(0);
			if (newErrors.paymentAmount || newErrors.paymentPercentage) {
				const paymentIdx = applicableSteps.findIndex((s) => s.key === 'paymentAndConditions');
				if (paymentIdx >= 0) setActiveStep(paymentIdx);
			}
			return;
		}

		try {
			if (setIsLoading) setIsLoading(true);

			const shouldIncludePayment =
				values.conditionType === 'contract' ||
				(values.conditionType === 'workPlan' && values.hasPaymentTerms);

			const paymentAmount =
				shouldIncludePayment && values.paymentType === 'fixed' && values.paymentAmount !== ''
					? Number(values.paymentAmount)
					: null;
			const paymentPercentage =
				shouldIncludePayment &&
				values.paymentType === 'percentage' &&
				values.paymentPercentage !== ''
					? Number(values.paymentPercentage)
					: null;

			// All requirements live inside verificationSections
			const basePayload: Partial<ConditionPayload> = {
				description: values.description || null,
				name: values.name,
				paymentAmount,
				paymentPercentage,
				sequenceOrder: values.sequenceOrder ? Number(values.sequenceOrder) : undefined,
				verificationSections:
					values.verificationSections.length > 0 ? values.verificationSections : null,
			};

			let res;
			if (isEditMode && condition) {
				const updatePayload: Partial<ConditionPayload> = {
					...basePayload,
					conditionType: values.conditionType === 'contract' ? 'payment' : 'base',
				};
				if (values.conditionType === 'workPlan') {
					updatePayload.workPlanId = values.workPlanId || null;
					updatePayload.activityId = null;
					updatePayload.contractId = null;
				} else {
					updatePayload.workPlanId =
						typeof values.workPlanId === 'number' ? values.workPlanId : null;
					updatePayload.activityId =
						typeof values.activityId === 'number' ? values.activityId : null;
					updatePayload.contractId =
						typeof values.contractId === 'number' ? values.contractId : null;
				}
				res = await updateCondition(condition.id, updatePayload);
			} else {
				const createPayload: ConditionPayload = {
					...basePayload,
					name: values.name,
					conditionType: values.conditionType === 'contract' ? 'payment' : 'base',
				};
				if (values.conditionType === 'workPlan') {
					createPayload.workPlanId = values.workPlanId || null;
				} else {
					createPayload.workPlanId =
						typeof values.workPlanId === 'number' ? values.workPlanId : null;
					createPayload.activityId =
						typeof values.activityId === 'number' ? values.activityId : null;
					createPayload.contractId =
						typeof values.contractId === 'number' ? values.contractId : null;
				}
				res = await createCondition(createPayload);
			}

			if (res.error) throw new Error(res.error.message);

			if (onSuccess) onSuccess();
			setBanner({
				hasClose: true,
				message: `Condition "${values.name}" ${isEditMode ? 'updated' : 'created'} successfully`,
				severity: 'success',
			});
			if (onClose) onClose();
		} catch (err) {
			setError(
				err instanceof Error
					? err.message
					: `Failed to ${isEditMode ? 'update' : 'create'} condition`
			);
		} finally {
			setIsSubmitting(false);
			if (setIsLoading) setIsLoading(false);
		}
	};

	// Render: Step 1 — Milestone Details

	const renderStepDetails = () => (
		<Stack spacing={2.5} sx={{ width: '100%' }}>
			{/* Quick-set name presets */}
			{!values.name.trim() && (
				<Box>
					<Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.75 }}>
						{t('spendPlan.conditions.wizard.quickSelect')}
					</Typography>
					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
						{CONDITION_NAME_PRESETS.map((preset) => (
							<Chip
								key={preset}
								clickable
								label={preset}
								onClick={() => setValues((prev) => ({ ...prev, name: preset }))}
								size="small"
								variant="outlined"
								sx={{
									borderColor: 'grey.300',
									'&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
								}}
							/>
						))}
					</Box>
				</Box>
			)}

			{/* Name */}
			<Box>
				<Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
					{t('spendPlan.conditions.wizard.milestoneName')}{' '}
					<span style={{ color: '#d32f2f' }}>*</span>
				</Typography>
				<TextField
					autoFocus
					error={Boolean(errors.name)}
					fullWidth
					helperText={errors.name || t('spendPlan.conditions.wizard.milestoneNameHelper')}
					name="name"
					onChange={handleChange}
					placeholder={t('spendPlan.conditions.wizard.milestoneNamePlaceholder')}
					value={values.name}
				/>
			</Box>

			{/* Sequence Order */}
			<Box>
				<Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
					{t('spendPlan.conditions.wizard.sequenceOrder')}
				</Typography>
				<TextField
					fullWidth
					error={Boolean(errors.sequenceOrder)}
					helperText={
						errors.sequenceOrder ||
						(existingConditionsCount > 0
							? t('spendPlan.conditions.wizard.sequenceHelperWithCount', {
									count: existingConditionsCount,
								})
							: t('spendPlan.conditions.wizard.sequenceHelper'))
					}
					name="sequenceOrder"
					onChange={handleChange}
					placeholder={t('spendPlan.conditions.wizard.sequencePlaceholder')}
					type="number"
					inputProps={{ min: 1, step: 1 }}
					value={values.sequenceOrder}
				/>
			</Box>

			{/* Description (optional) */}
			<Box>
				<Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
					{t('spendPlan.conditions.wizard.descriptionOptional')}
				</Typography>
				<TextField
					fullWidth
					helperText={t('spendPlan.conditions.wizard.descriptionHelper')}
					minRows={2}
					multiline
					name="description"
					onChange={handleChange}
					placeholder={t('spendPlan.conditions.wizard.descriptionPlaceholder')}
					value={values.description}
				/>
			</Box>

			{/* Work plan conditions — toggle payment terms */}
			{values.conditionType === 'workPlan' && (
				<FormControlLabel
					control={
						<Checkbox
							checked={values.hasPaymentTerms}
							name="hasPaymentTerms"
							onChange={handleCheckboxChange}
						/>
					}
					label="Include payment terms for this prerequisite"
				/>
			)}

			{/* Parent hierarchy selectors — hidden when context is already known */}
			{values.conditionType === 'contract' && !hasPrePopulatedContext && (
				<Stack spacing={2}>
					<Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
						{t('spendPlan.conditions.wizard.contractHierarchy')}
					</Typography>
					<TextField
						error={Boolean(errors.workPlanId)}
						fullWidth
						helperText={errors.workPlanId || t('spendPlan.conditions.wizard.selectWorkPlan')}
						label={t('workPlan')}
						name="workPlanId"
						onChange={(e) => {
							const val = e.target.value ? Number(e.target.value) : '';
							setValues((prev) => ({ ...prev, workPlanId: val, activityId: '', contractId: '' }));
						}}
						select
						value={values.workPlanId}
						size="small">
						{workPlans.map((wp) => (
							<MenuItem key={wp.id} value={wp.id}>
								{wp.name}
							</MenuItem>
						))}
					</TextField>
					<TextField
						disabled={!values.workPlanId}
						error={Boolean(errors.activityId)}
						fullWidth
						helperText={errors.activityId || t('spendPlan.conditions.wizard.selectActivity')}
						label={t('activity')}
						name="activityId"
						onChange={(e) => {
							const val = e.target.value ? Number(e.target.value) : '';
							setValues((prev) => ({ ...prev, activityId: val, contractId: '' }));
						}}
						select
						value={values.activityId}
						size="small">
						{filteredActivities.map((a) => (
							<MenuItem key={a.id} value={a.id}>
								{a.name}
							</MenuItem>
						))}
					</TextField>
					<TextField
						disabled={!values.activityId}
						error={Boolean(errors.contractId)}
						fullWidth
						helperText={errors.contractId || t('spendPlan.conditions.wizard.selectContract')}
						label={t('contract')}
						name="contractId"
						onChange={(e) => {
							const val = e.target.value ? Number(e.target.value) : '';
							setValues((prev) => ({ ...prev, contractId: val }));
						}}
						select
						value={values.contractId}
						size="small">
						{filteredContracts.map((c) => (
							<MenuItem key={c.id} value={c.id}>
								{getContractNum(c)} — {getContractSupplierName(c)}
							</MenuItem>
						))}
					</TextField>
				</Stack>
			)}

			{values.conditionType === 'workPlan' && (
				<TextField
					error={Boolean(errors.workPlanId)}
					fullWidth
					helperText={errors.workPlanId || t('spendPlan.conditions.wizard.selectWorkPlan')}
					label={t('workPlan')}
					name="workPlanId"
					onChange={(e) => {
						const val = e.target.value ? Number(e.target.value) : '';
						setValues((prev) => ({ ...prev, workPlanId: val }));
					}}
					select
					value={values.workPlanId}
					size="small">
					{workPlans.map((wp) => (
						<MenuItem key={wp.id} value={wp.id}>
							{wp.name}
						</MenuItem>
					))}
				</TextField>
			)}
		</Stack>
	);

	// Render: Step 2 — Payment Terms & Conditions (merged)

	const renderStepPaymentAndConditions = () => {
		// Milestones are denominated in the contract's own currency (matching the
		// header), so the label and the basis both use the contract's primary
		// currency and the commitment/contract amount resolved in that currency.
		const contractCurrency = selectedContract?.amounts?.[0]?.currency || 'USD';
		const contractAmount: number | null = selectedContract
			? getContractCommitmentAmount(selectedContract, { currency: contractCurrency })
			: null;

		return (
			<Stack spacing={3} sx={{ width: '100%' }}>
				{/* ---- PAYMENT TERMS ---- */}
				<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
					{t('spendPlan.conditions.wizard.paymentTerms')}
				</Typography>
				<Typography color="text.secondary">
					{t('spendPlan.conditions.wizard.paymentTermsHelper')}
				</Typography>

				{/* Contract context */}
				{selectedContract && (
					<Paper
						elevation={0}
						sx={{
							bgcolor: 'info.lighter',
							border: '1px solid',
							borderColor: 'info.light',
							borderRadius: 2,
							p: 2,
						}}>
						<Stack spacing={1}>
							<Typography variant="body2" sx={{ fontWeight: 500 }}>
								{t('spendPlan.conditions.wizard.contractLabel')} {getContractNum(selectedContract)}
							</Typography>
							<Typography variant="body2">
								{t('spendPlan.conditions.wizard.contractValueLabel')}{' '}
								{contractAmount != null
									? formatCurrency({ currency: contractCurrency, value: contractAmount })
									: '—'}
							</Typography>
							{contractPaymentTotal.totalPercentage > 0 && (
								<Typography
									variant="body2"
									color={contractPaymentTotal.totalPercentage > 100 ? 'error' : 'text.secondary'}>
									{t('spendPlan.conditions.wizard.alreadyAllocated', {
										pct: contractPaymentTotal.totalPercentage.toFixed(1),
										remaining: (100 - contractPaymentTotal.totalPercentage).toFixed(1),
									})}
								</Typography>
							)}
						</Stack>
					</Paper>
				)}

				{/* Payment Type */}
				<FormControl>
					<RadioGroup name="paymentType" onChange={handleChange} row value={values.paymentType}>
						<FormControlLabel
							control={<Radio />}
							label={t('spendPlan.conditions.wizard.percentageOfContract')}
							value="percentage"
						/>
						<FormControlLabel
							control={<Radio />}
							label={t('spendPlan.conditions.wizard.fixedAmount')}
							value="fixed"
						/>
					</RadioGroup>
				</FormControl>

				{/* Percentage input */}
				{values.paymentType === 'percentage' && (
					<TextField
						error={!!errors.paymentPercentage}
						fullWidth
						helperText={
							errors.paymentPercentage ||
							(contractAmount && values.paymentPercentage
								? `= ${formatCurrency({
										currency: contractCurrency,
										value: (contractAmount * Number(values.paymentPercentage)) / 100,
									})}`
								: t('spendPlan.conditions.wizard.percentageHelper'))
						}
						InputProps={{
							endAdornment: <InputAdornment position="end">%</InputAdornment>,
						}}
						label={t('spendPlan.conditions.wizard.paymentPercentage')}
						name="paymentPercentage"
						onChange={handleChange}
						type="number"
						value={values.paymentPercentage}
					/>
				)}

				{/* Fixed amount input */}
				{values.paymentType === 'fixed' && (
					<TextField
						error={!!errors.paymentAmount}
						fullWidth
						helperText={
							errors.paymentAmount || t('spendPlan.conditions.wizard.paymentAmountHelper')
						}
						InputProps={{
							startAdornment: <InputAdornment position="start">{contractCurrency}</InputAdornment>,
						}}
						label={t('spendPlan.conditions.wizard.paymentAmount')}
						name="paymentAmount"
						onChange={handleChange}
						type="number"
						value={values.paymentAmount}
					/>
				)}

				{/* ---- DIVIDER ---- */}
				<Divider sx={{ my: 1 }} />

				{/* ---- CONTRACT DOCUMENTS ---- */}
				{values.conditionType === 'contract' && values.contractId && (
					<>
						<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
							{t('spendPlan.conditions.wizard.contractDocuments')}
						</Typography>
						<ContractDocumentsSection
							contractId={typeof values.contractId === 'number' ? values.contractId : 0}
							onDocumentInsightsLoaded={handleDocumentInsightsLoaded}
							onInsightsLoadingChange={setInsightsLoading}
						/>
					</>
				)}

				{/* ---- AI CONDITION GENERATION ---- */}
				{values.conditionType === 'contract' && values.contractId && (
					<AiConditionGenerator
						contractId={typeof values.contractId === 'number' ? values.contractId : 0}
						contractName={
							selectedContract
								? getContractNum(selectedContract) || `Contract ${selectedContract.id}`
								: ''
						}
						contractAmount={contractAmount ?? 0}
						contractCurrency={contractCurrency}
						existingConditions={conditions.filter((c) => {
							const condContractId = typeof c.contract === 'number' ? c.contract : c.contract?.id;
							return condContractId === values.contractId;
						})}
						documentInsights={documentInsights}
						insightsLoading={insightsLoading}
						onAddAiSections={handleUseAiConditions}
					/>
				)}

				{/* ---- DIVIDER ---- */}
				<Divider sx={{ my: 1 }} />

				{/* ---- CONDITION SETTING (verification sections) ---- */}
				<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
					{t('spendPlan.conditions.wizard.conditionSetting')}
				</Typography>
				<ConditionSettingsEditor
					verificationSections={values.verificationSections}
					onChange={(sections) =>
						setValues((prev) => ({ ...prev, verificationSections: sections }))
					}
				/>
			</Stack>
		);
	};

	// Render: Step 3 — Review & Confirm

	const renderStepReview = () => {
		const contractCurrency = selectedContract?.amounts?.[0]?.currency || 'USD';
		const contractAmount: number | null = selectedContract
			? getContractCommitmentAmount(selectedContract, { currency: contractCurrency })
			: null;
		const calculatedAmount =
			values.paymentType === 'percentage' && values.paymentPercentage && contractAmount != null
				? (contractAmount * Number(values.paymentPercentage)) / 100
				: values.paymentAmount
					? Number(values.paymentAmount)
					: null;

		return (
			<Stack spacing={2} sx={{ width: '100%' }}>
				<Alert severity="info" variant="outlined">
					{t('spendPlan.conditions.wizard.review.alert', {
						action: isEditMode
							? t('spendPlan.conditions.wizard.review.alertUpdate')
							: t('spendPlan.conditions.wizard.review.alertCreate'),
					})}
				</Alert>

				{/* Summary Cards */}
				<Paper elevation={0} sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2 }}>
					<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
						{t('spendPlan.conditions.wizard.review.contractSection')}
					</Typography>
					<Stack spacing={0.5}>
						<Typography variant="body2">
							<strong>{t('spendPlan.conditions.wizard.review.workPlanLabel')}</strong>{' '}
							{selectedWorkPlan?.name || activeWorkPlan?.name || '—'}
						</Typography>
						{selectedActivity && (
							<Typography variant="body2">
								<strong>{t('spendPlan.conditions.wizard.review.activityLabel')}</strong>{' '}
								{selectedActivity.name}
							</Typography>
						)}
						{selectedContract && (
							<Typography variant="body2">
								<strong>{t('spendPlan.conditions.wizard.review.contractLabel')}</strong>{' '}
								{getContractNum(selectedContract)} ({getContractSupplierName(selectedContract)})
							</Typography>
						)}
					</Stack>
				</Paper>

				<Paper elevation={0} sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2 }}>
					<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
						{t('spendPlan.conditions.wizard.review.milestoneDetails')}
					</Typography>
					<Stack spacing={0.5}>
						<Typography variant="body2">
							<strong>{t('spendPlan.conditions.wizard.review.nameLabel')}</strong> {values.name}
						</Typography>
						{values.sequenceOrder && (
							<Typography variant="body2">
								<strong>{t('spendPlan.conditions.wizard.review.sequenceLabel')}</strong> #
								{values.sequenceOrder}
							</Typography>
						)}
						{values.description && (
							<Typography variant="body2">
								<strong>{t('spendPlan.conditions.wizard.review.descriptionLabel')}</strong>{' '}
								{values.description}
							</Typography>
						)}
					</Stack>
				</Paper>

				{/* Payment card */}
				{(values.conditionType === 'contract' || values.hasPaymentTerms) && (
					<Paper elevation={0} sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2 }}>
						<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
							{t('spendPlan.conditions.wizard.review.paymentTerms')}
						</Typography>
						<Stack spacing={0.5}>
							{values.paymentType === 'percentage' && values.paymentPercentage ? (
								<>
									<Typography variant="body2">
										<strong>{t('spendPlan.conditions.wizard.review.percentageLabel')}</strong>{' '}
										{values.paymentPercentage}%
									</Typography>
									{calculatedAmount !== null && (
										<Typography variant="body2">
											<strong>{t('spendPlan.conditions.wizard.review.calculatedLabel')}</strong>{' '}
											{formatCurrency({ currency: contractCurrency, value: calculatedAmount })}
										</Typography>
									)}
								</>
							) : values.paymentType === 'fixed' && values.paymentAmount ? (
								<Typography variant="body2">
									<strong>{t('spendPlan.conditions.wizard.review.fixedAmountLabel')}</strong>{' '}
									{formatCurrency({
										currency: contractCurrency,
										value: Number(values.paymentAmount),
									})}
								</Typography>
							) : (
								<Typography variant="body2" color="text.secondary">
									{t('spendPlan.conditions.wizard.review.noPaymentTerms')}
								</Typography>
							)}
						</Stack>
					</Paper>
				)}

				{/* Requirements card */}
				<Paper elevation={0} sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2 }}>
					<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
						{t('spendPlan.conditions.wizard.review.requirements')}
					</Typography>
					<Stack spacing={0.5}>
						{values.verificationSections.length > 0 ? (
							<>
								<Typography variant="body2">
									<strong>{t('spendPlan.conditions.wizard.review.sectionsLabel')}</strong>{' '}
									{values.verificationSections.length}
								</Typography>
								{values.verificationSections.map((section) => {
									const assignee = projectUsers.find((u) => u.id === section.assignedTo);
									return (
										<Box key={section.id} sx={{ ml: 2, mb: 1 }}>
											<Typography variant="body2" sx={{ fontWeight: 600 }}>
												{section.name || t('spendPlan.conditions.wizard.review.unnamedSection')}
												{assignee && (
													<Typography
														component="span"
														variant="body2"
														color="primary.main"
														sx={{ ml: 1 }}>
														→ {assignee.firstName} {assignee.lastName}
													</Typography>
												)}
											</Typography>
											{section.fields.map((f) => (
												<Typography key={f.id} variant="body2" sx={{ ml: 2 }}>
													&bull;{' '}
													{f.fieldType === 'document'
														? `Document: ${f.label || 'Untitled'}${f.maxDocuments === 0 ? ' (unlimited)' : f.maxDocuments && f.maxDocuments > 1 ? ` (max ${f.maxDocuments})` : ''}`
														: f.fieldType === 'yesno'
															? `Yes/No: ${f.label || 'Untitled'}`
															: `${f.fieldType}: ${f.label || 'Untitled'}`}
													{f.required ? ' (required)' : ''}
													{f.required && f.allowOverride ? ' — overridable with comment' : ''}
												</Typography>
											))}
											{section.fields.length === 0 && (
												<Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
													No requirements in this section
												</Typography>
											)}
										</Box>
									);
								})}
							</>
						) : (
							<Typography variant="body2" color="text.secondary">
								No requirements configured
							</Typography>
						)}
					</Stack>
				</Paper>
			</Stack>
		);
	};

	// Render current step content

	const renderStepContent = () => {
		switch (currentStepDef?.key) {
			case 'details':
				return renderStepDetails();
			case 'paymentAndConditions':
				return renderStepPaymentAndConditions();
			case 'review':
				return renderStepReview();
			default:
				return null;
		}
	};

	// Main render

	return (
		<Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
			{/* Stepper — fixed header */}
			<Stepper
				activeStep={activeStep}
				alternativeLabel
				sx={{
					flexShrink: 0,
					pb: 3,
					pt: 1,
				}}>
				{applicableSteps.map((step, index) => (
					<Step
						key={step.key}
						completed={index < activeStep}
						sx={{ cursor: isEditMode || index <= activeStep ? 'pointer' : 'default' }}
						onClick={() => handleStepClick(index)}>
						<StepLabel>{t(step.labelKey || step.label)}</StepLabel>
					</Step>
				))}
			</Stepper>

			{/* Scrollable body */}
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					overflowY: 'auto',
					overflowX: 'hidden',
					px: 0.5,
					width: '100%',
				}}>
				{/* Error */}
				{error && (
					<Alert ref={errorRef} severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				{/* Step Content */}
				{renderStepContent()}
			</Box>

			{/* Navigation Buttons — fixed footer */}
			<Stack
				direction="row"
				justifyContent="space-between"
				sx={{
					flexShrink: 0,
					pt: 2,
					pb: 1,
					borderTop: '1px solid',
					borderColor: 'divider',
				}}>
				{activeStep === 0 ? (
					<Button onClick={onClose} startIcon={<IconClose />} type="button" variant="outlined">
						{t('cancel')}
					</Button>
				) : (
					<Button onClick={handleBack} startIcon={<IconBack />} type="button" variant="outlined">
						{t('back')}
					</Button>
				)}

				{currentStepDef?.key === 'review' ? (
					<LoadingButton
						loading={isSubmitting}
						onClick={handleSubmit}
						type="button"
						variant="contained"
						color="primary">
						{isEditMode
							? t('spendPlan.conditions.wizard.saveChanges')
							: t('spendPlan.conditions.wizard.createCondition')}
					</LoadingButton>
				) : (
					<Button
						disabled={!canGoNext}
						endIcon={<IconNext />}
						onClick={handleNext}
						type="button"
						variant="contained">
						{t('next')}
					</Button>
				)}
			</Stack>
		</Box>
	);
};
